import { useEffect, useRef, useState } from 'react';
import type {
  MediaResponse,
  UploadMediaCommandInput,
  UploadMediaCommandOutput,
  DeleteMediaCommandInput,
  DeleteMediaCommandOutput,
} from '@amzn/gbsa-helios-typescript-client';

import { useUploadMediaMutation } from './useUploadMediaMutation';
import { useDeleteMediaMutation } from './useDeleteMediaMutation';
import { fileToUploadMediaInput, type UploadMediaContext } from './mediaContentType';

/** Media ids of successful uploads, keyed by the picked File. */
type UploadedIdsByFile = ReadonlyMap<File, string>;

export interface UseMediaUploadsInput {
  /** Already-persisted media for the entity (task, audit response, ...). */
  existingMedia: MediaResponse[];
  /** Identity fields stamped onto uploads/deletes (program, BU, entity type, user). */
  uploadContext: UploadMediaContext;
  /**
   * Called with the effective media id list (existing minus removed, plus
   * newly uploaded) whenever it changes. Parents include these ids in
   * their save payload.
   */
  onMediaIdsChange: (mediaIds: string[]) => void;
  /**
   * Called with the ids of existing (persisted) media the user removed.
   * Removal is UI-only until save: the page deletes these media objects
   * after the save succeeds, so backing out of the form restores them.
   */
  onRemovedMediaIdsChange: (mediaIds: readonly string[]) => void;
  /** True while any upload is in flight — parents should block saves. */
  onUploadingChange: (isUploading: boolean) => void;
  /** Upload/delete failures — parents surface via their error banner. */
  onError: (error: unknown) => void;
}

export interface UseMediaUploadsResult {
  /** Pending picker files (uploaded but not yet persisted via save). */
  files: File[];
  /** Ids of existing media the user removed (staged until save). */
  removedExistingIds: readonly string[];
  /** Wire to the file picker; uploads additions, cleans up removals. */
  handleFilesChange: (updatedFiles: File[]) => void;
  /** Stage removal of an existing (persisted) media object. */
  removeExistingMedia: (mediaId: string) => void;
  /** True while at least one file upload is in flight. */
  isUploading: boolean;
}

/**
 * Media upload lifecycle for attachment UIs, shared across features
 * (task performance form, audit responses).
 *
 * Owns the whole state machine so components stay render-only:
 * - Files upload immediately on selection (desktop convention).
 * - Removing a pending (unsaved) file deletes its media object right
 *   away — nothing references it yet. If its upload is still in flight,
 *   the completion handler deletes the object instead of recording it.
 * - Removing existing media is staged: the id is reported upward and the
 *   page deletes the object only after a successful save.
 * - The effective id list is derived in one effect from latest committed
 *   state and always intersected with the current file list, so an async
 *   completion can never resurrect a withdrawn file or removal.
 */
export function useMediaUploads({
  existingMedia,
  uploadContext,
  onMediaIdsChange,
  onRemovedMediaIdsChange,
  onUploadingChange,
  onError,
}: UseMediaUploadsInput): UseMediaUploadsResult {
  const [uploadMedia] = useUploadMediaMutation();
  const [deleteMedia] = useDeleteMediaMutation();

  const [files, setFiles] = useState<File[]>([]);
  const [removedExistingIds, setRemovedExistingIds] = useState<readonly string[]>([]);
  const [uploadedIds, setUploadedIds] = useState<UploadedIdsByFile>(() => new Map());
  const [uploadsInFlight, setUploadsInFlight] = useState(0);
  // Files removed from the picker while their upload was still in flight.
  // The completion handler checks this to undo its own side effect (delete
  // the just-uploaded media) instead of recording an id nobody wants.
  const cancelledUploadsRef = useRef<Set<File>>(new Set());

  // After a save the entity refetches and freshly uploaded media come
  // back in existingMedia (with presigned URLs). Drop those files from the
  // local picker so each attachment renders exactly once — render-time
  // state adjustment, per the hooks-extra lint rule. The emit effect below
  // re-fires, but the id list content is unchanged (ids simply move from
  // "uploaded" to "existing").
  const existingIds = new Set(existingMedia.map((entry) => entry.media.mediaId));
  const persistedFiles = files.filter((file) => {
    const uploadedId = uploadedIds.get(file);
    return uploadedId !== undefined && existingIds.has(uploadedId);
  });
  if (persistedFiles.length > 0) {
    setFiles((current) => current.filter((file) => !persistedFiles.includes(file)));
    setUploadedIds((current) => {
      const next = new Map(current);
      for (const file of persistedFiles) next.delete(file);
      return next;
    });
  }

  // Report the effective id list from the latest committed state. Deriving
  // in one effect — instead of imperative emits inside handlers and state
  // updaters — means an async upload completing can never overwrite a
  // removal with the stale snapshot it closed over, and the updaters stay
  // pure (StrictMode double-invokes them).
  useEffect(() => {
    const keptExisting: string[] = existingMedia
      .map((entry) => entry.media.mediaId)
      .filter((id) => !removedExistingIds.includes(id));
    // Intersect with `files` (current intent) instead of trusting the
    // completion map alone: an upload that resolves after its file was
    // removed can never leak its id into the payload.
    const activeUploadedIds = files
      .map((file) => uploadedIds.get(file))
      .filter((id): id is string => id !== undefined);
    onMediaIdsChange([...keptExisting, ...activeUploadedIds]);
    onRemovedMediaIdsChange(removedExistingIds);
  }, [
    existingMedia,
    removedExistingIds,
    uploadedIds,
    files,
    onMediaIdsChange,
    onRemovedMediaIdsChange,
  ]);

  // Effect-derived, matching the emit effect above: updaters stay pure
  // (StrictMode double-invokes them) and the parent always sees the
  // latest committed count.
  useEffect(() => {
    onUploadingChange(uploadsInFlight > 0);
  }, [uploadsInFlight, onUploadingChange]);

  const handleFilesChange = createFilesChangeHandler({
    files,
    uploadedIds,
    uploadContext,
    cancelledUploads: cancelledUploadsRef.current,
    uploadMedia,
    deleteMedia,
    setFiles,
    setUploadedIds,
    trackUpload: (delta) => setUploadsInFlight((current) => current + delta),
    onError,
  });

  // UI-only: the actual DeleteMediaCommand runs at the page level after a
  // successful save, so abandoning the form leaves the media intact.
  const removeExistingMedia = (mediaId: string): void => {
    setRemovedExistingIds((current) => [...current, mediaId]);
  };

  return {
    files,
    removedExistingIds,
    handleFilesChange,
    removeExistingMedia,
    isUploading: uploadsInFlight > 0,
  };
}

interface FilesChangeDeps {
  files: File[];
  uploadedIds: UploadedIdsByFile;
  uploadContext: UploadMediaContext;
  cancelledUploads: Set<File>;
  uploadMedia: (input: UploadMediaCommandInput) => Promise<UploadMediaCommandOutput>;
  deleteMedia: (input: DeleteMediaCommandInput) => Promise<DeleteMediaCommandOutput>;
  setFiles: React.Dispatch<React.SetStateAction<File[]>>;
  setUploadedIds: React.Dispatch<React.SetStateAction<UploadedIdsByFile>>;
  trackUpload: (delta: number) => void;
  onError: (error: unknown) => void;
}

/**
 * Builds the picker-change handler. Module-level (rebuilt per render with
 * fresh deps) so the upload/removal branching keeps its own complexity
 * budget separate from the hook body.
 */
function createFilesChangeHandler(deps: FilesChangeDeps) {
  const {
    files,
    uploadedIds,
    uploadContext,
    cancelledUploads,
    uploadMedia,
    deleteMedia,
    setFiles,
    setUploadedIds,
    trackUpload,
    onError,
  } = deps;
  const { programId, createdBy: deletedBy } = uploadContext;

  const uploadFile = async (file: File): Promise<void> => {
    trackUpload(1);
    try {
      const input = await fileToUploadMediaInput(file, uploadContext);
      const response = await uploadMedia(input);
      const mediaId = response?.media?.mediaId;
      if (!mediaId) {
        // Contract violation: the upload "succeeded" without an id. A
        // silent skip would leave the file visible in the picker but
        // absent from the payload — surface it like any failed upload.
        setFiles((current) => current.filter((f) => f !== file));
        onError(new Error('Upload response is missing mediaId'));
        return;
      }
      if (cancelledUploads.has(file)) {
        // Intent withdrawn mid-flight (file removed from the picker
        // while uploading): undo the side effect instead of recording
        // an id the emit would have to filter forever.
        deleteMedia({ programId, mediaId, deletedBy }).catch(onError);
      } else {
        setUploadedIds((current) => new Map(current).set(file, mediaId));
      }
    } catch (err) {
      // Drop the failed file from the picker so the list reflects reality.
      setFiles((current) => current.filter((f) => f !== file));
      onError(err);
    } finally {
      cancelledUploads.delete(file);
      trackUpload(-1);
    }
  };

  return (updatedFiles: File[]): void => {
    // A new selection starts a fresh attempt — clear any stale error so a
    // failed upload's banner doesn't outlive the problem.
    onError(undefined);
    setFiles(updatedFiles);

    // Files removed via the picker: delete their uploaded media objects
    // immediately — they were never saved, so nothing references them.
    for (const removedFile of files.filter((f) => !updatedFiles.includes(f))) {
      const mediaId = uploadedIds.get(removedFile);
      if (mediaId) {
        setUploadedIds((current) => {
          const next = new Map(current);
          next.delete(removedFile);
          return next;
        });
        deleteMedia({ programId, mediaId, deletedBy }).catch(onError);
      } else {
        // Upload still in flight — no media id to delete yet. Mark the
        // intent withdrawn; the completion handler cleans up the object.
        cancelledUploads.add(removedFile);
      }
    }

    // Newly added files: upload immediately.
    for (const addedFile of updatedFiles.filter((f) => !files.includes(f))) {
      void uploadFile(addedFile);
    }
  };
}
